import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FiLink, FiMessageSquare, FiPlus, FiRefreshCw, FiSearch, FiSend, FiUser, FiX } from "react-icons/fi";
import {
  createCrmContact,
  createCrmConversation,
  createCrmConversationMessage,
  createCrmConversationSummary,
  createCrmTask,
  fetchCrmCompanies,
  fetchCrmContacts,
  fetchCrmConversation,
  fetchCrmConversations,
  fetchCrmLeads,
  fetchCrmTasks,
  relinkCrmConversationContact,
  sendCrmWhatsAppMessage,
  sendCrmWhatsAppTemplate,
} from "../crmApi";
import { useCrmCopy } from "../crmI18n";
import { usePmsTheme } from "../../Components/PmsThemeContext";

const emptyConversationForm = {
  subject: "",
  channel: "manual",
  status: "open",
  provider: "",
  contact_customer_id: "",
  company_id: "",
  lead_id: "",
};

const emptyMessageForm = {
  direction: "internal",
  message_type: "note",
  body: "",
};

const emptyTaskForm = {
  title: "",
  task_type: "follow_up",
  priority: "normal",
  due_at: "",
  description: "",
};

const emptyWhatsAppTemplateForm = {
  template_name: "",
  language_code: "es_ES",
  parameters: "",
};

const emptyContactForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  company_id: "",
  job_title: "",
  notes: "",
  source: "call",
  crm_status: "active",
};

function getContactLabel(contact) {
  if (!contact) return "";
  const fullName = contact?.full_name || [contact?.first_name, contact?.last_name].filter(Boolean).join(" ");
  return fullName || contact?.email || contact?.phone || "";
}

function getConversationContactLabel(conversation, copy) {
  return getContactLabel(conversation?.contact) || copy.common.noLinkedContact || copy.common.noRecords;
}

function getRelatedContextLabel(conversation) {
  return conversation?.lead?.title || conversation?.company?.name || "";
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function cleanText(value) {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

function humanizeSummaryKey(key) {
  return cleanText(key).replace(/[_-]+/g, " ");
}

function formatAiSummaryValue(value) {
  if (value === undefined || value === null) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    const text = cleanText(value);
    return text === "[object Object]" ? "" : text;
  }
  if (Array.isArray(value)) {
    return value.map(formatAiSummaryValue).filter(Boolean).join("\n");
  }
  if (typeof value === "object") {
    return Object.entries(value)
      .map(([key, item]) => {
        const text = formatAiSummaryValue(item);
        return text ? `${humanizeSummaryKey(key)}: ${text}` : "";
      })
      .filter(Boolean)
      .join("\n");
  }
  return cleanText(value);
}

function getCallEventLabel(eventType, copy) {
  const normalized = cleanText(eventType).toUpperCase();
  if (!normalized) return "";
  return copy.callLog?.events?.[normalized] || normalized.replace(/^NOTIFY_/, "").replace(/_/g, " ").toLowerCase();
}

function getCallDispositionLabel(disposition, copy) {
  const normalized = cleanText(disposition).toLowerCase();
  if (!normalized) return "";
  return copy.callLog?.dispositions?.[normalized] || normalized.replace(/_/g, " ");
}

function getPayloadEventType(message) {
  const event = cleanText(message?.external_payload?.event).toUpperCase();
  if (event) return event;
  const match = cleanText(message?.body).match(/\bNOTIFY_[A-Z_]+\b/);
  return match?.[0] || "";
}

function getCallPhone(message, direction) {
  const payload = message?.external_payload || {};
  if (direction === "outgoing") {
    return (
      cleanText(message?.recipient_phone) ||
      cleanText(payload.destination) ||
      cleanText(payload.called_did) ||
      cleanText(payload.caller_id)
    );
  }

  return (
    cleanText(message?.sender_phone) ||
    cleanText(payload.caller_id) ||
    cleanText(payload.destination) ||
    cleanText(payload.called_did)
  );
}

function getConversationPhone(conversation) {
  const messages = Array.isArray(conversation?.messages) ? conversation.messages : [];
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    const direction = message?.direction === "outgoing" ? "outgoing" : "incoming";
    const phone = getCallPhone(message, direction);
    if (phone) return phone;
  }

  return "";
}

function formatCallMessageBody(message, copy) {
  const eventType = getPayloadEventType(message);
  const isZadarmaCall =
    message?.provider === "zadarma" ||
    eventType ||
    /\bNOTIFY_[A-Z_]+\b/.test(cleanText(message?.body));

  if (!isZadarmaCall) return message?.body || "";

  const payload = message?.external_payload || {};
  const direction = message?.direction === "outgoing" ? "outgoing" : "incoming";
  const phone = getCallPhone(message, direction);
  const internal = cleanText(payload.internal || payload.last_internal);
  const disposition = getCallDispositionLabel(payload.disposition, copy);
  const duration = Number.isInteger(Number(message?.duration_seconds ?? payload.duration))
    ? Number(message?.duration_seconds ?? payload.duration)
    : null;
  const parts = [getCallEventLabel(eventType, copy) || message?.body || ""];

  if (phone) parts.push(`${copy.callLog?.labels?.number || "Number"}: ${phone}`);
  if (disposition) parts.push(`${copy.callLog?.labels?.result || "Result"}: ${disposition}`);
  if (duration !== null) parts.push(`${copy.callLog?.labels?.duration || "Duration"}: ${duration}s`);
  if (internal) parts.push(`${copy.callLog?.labels?.extension || "Extension"}: ${internal}`);
  if (eventType === "NOTIFY_RECORD" && payload.call_id_with_rec) {
    parts.push(`${copy.callLog?.labels?.recordingId || "Recording ID"}: ${payload.call_id_with_rec}`);
  }

  return parts.filter(Boolean).join(". ") + ".";
}

function isWhatsAppConversation(conversation) {
  return conversation?.channel === "whatsapp" || conversation?.provider === "crm_whatsapp";
}

function isImageAttachment(attachment) {
  return String(attachment?.mime_type || "").toLowerCase().startsWith("image/");
}

function getAttachmentLabel(attachment, copy) {
  return attachment?.file_name || attachment?.mime_type || copy.entities.activity.attachmentLabel;
}

export default function Activity() {
  const copy = useCrmCopy();
  const { languageId } = usePmsTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageCopy = copy.entities.activity;
  const [rows, setRows] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [relatedTasks, setRelatedTasks] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [leads, setLeads] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [messageSaving, setMessageSaving] = useState(false);
  const [whatsAppBody, setWhatsAppBody] = useState("");
  const [whatsAppSending, setWhatsAppSending] = useState(false);
  const [whatsAppTemplateOpen, setWhatsAppTemplateOpen] = useState(false);
  const [whatsAppTemplateForm, setWhatsAppTemplateForm] = useState(emptyWhatsAppTemplateForm);
  const [whatsAppTemplateSending, setWhatsAppTemplateSending] = useState(false);
  const [summarySaving, setSummarySaving] = useState(false);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [conversationForm, setConversationForm] = useState(emptyConversationForm);
  const [messageForm, setMessageForm] = useState(emptyMessageForm);
  const [taskForm, setTaskForm] = useState(emptyTaskForm);
  const [taskSaving, setTaskSaving] = useState(false);
  const [relinkingContact, setRelinkingContact] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactForm, setContactForm] = useState(emptyContactForm);
  const [contactSaving, setContactSaving] = useState(false);
  const [notice, setNotice] = useState("");

  const channelOptions = useMemo(
    () =>
      ["manual", "call", "whatsapp", "email", "meeting", "note"].map((channel) => ({
        value: channel,
        label: copy.conversationChannels?.[channel] || channel,
      })),
    [copy.conversationChannels]
  );

  const directionOptions = useMemo(
    () =>
      ["internal", "incoming", "outgoing"].map((direction) => ({
        value: direction,
        label: copy.conversationDirections?.[direction] || direction,
      })),
    [copy.conversationDirections]
  );

  const messageTypeOptions = useMemo(
    () =>
      ["note", "text", "call", "meeting", "whatsapp", "email"].map((type) => ({
        value: type,
        label: copy.conversationMessageTypes?.[type] || type,
      })),
    [copy.conversationMessageTypes]
  );

  const statusOptions = useMemo(
    () => [
      { value: "open", label: pageCopy.status.open },
      { value: "closed", label: pageCopy.status.closed },
    ],
    [pageCopy.status.closed, pageCopy.status.open]
  );

  const taskTypeOptions = useMemo(
    () =>
      ["follow_up", "call", "email", "whatsapp", "meeting", "todo"].map((type) => ({
        value: type,
        label: copy.taskTypes?.[type] || type,
      })),
    [copy.taskTypes]
  );

  const taskPriorityOptions = useMemo(
    () =>
      ["low", "normal", "high", "urgent"].map((priority) => ({
        value: priority,
        label: copy.taskPriorities?.[priority] || priority,
      })),
    [copy.taskPriorities]
  );

  const loadConversations = useCallback(async () => {
    setLoading(true);
    setError("");
    setNotice("");
    try {
      const data = await fetchCrmConversations({ q: query, limit: 100 });
      const nextRows = Array.isArray(data?.rows) ? data.rows : [];
      setRows(nextRows);
      if (selectedConversation?.id && !nextRows.some((row) => row.id === selectedConversation.id)) {
        setSelectedConversation(null);
        setRelatedTasks([]);
      }
    } catch (err) {
      console.error("[CRM] Conversations load error:", err);
      setError(err?.response?.data?.error || pageCopy.loadError);
    } finally {
      setLoading(false);
    }
  }, [pageCopy.loadError, query, selectedConversation?.id]);

  const loadLookups = useCallback(async () => {
    try {
      const [contactsData, companiesData, leadsData] = await Promise.all([
        fetchCrmContacts({ limit: 200 }),
        fetchCrmCompanies({ limit: 200 }),
        fetchCrmLeads({ limit: 200 }),
      ]);
      setContacts(Array.isArray(contactsData?.rows) ? contactsData.rows : []);
      setCompanies(Array.isArray(companiesData?.rows) ? companiesData.rows : []);
      setLeads(Array.isArray(leadsData?.rows) ? leadsData.rows : []);
    } catch (err) {
      console.error("[CRM] Activity lookups load error:", err);
      setContacts([]);
      setCompanies([]);
      setLeads([]);
    }
  }, []);

  const loadConversationDetail = useCallback(async (conversationId) => {
    if (!conversationId) return;
    setDetailLoading(true);
    setError("");
    setNotice("");
    try {
      const [data, tasksData] = await Promise.all([
        fetchCrmConversation(conversationId),
        fetchCrmTasks({ conversation_id: conversationId, status: "open", limit: 20 }),
      ]);
      setSelectedConversation(data);
      setRelatedTasks(Array.isArray(tasksData?.rows) ? tasksData.rows : []);
    } catch (err) {
      console.error("[CRM] Conversation detail load error:", err);
      setError(err?.response?.data?.error || pageCopy.loadError);
    } finally {
      setDetailLoading(false);
    }
  }, [pageCopy.loadError]);

  useEffect(() => {
    const conversationId = searchParams.get("conversation_id");
    if (!conversationId) return;
    loadConversationDetail(conversationId);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete("conversation_id");
      return next;
    }, { replace: true });
  }, [loadConversationDetail, searchParams, setSearchParams]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    setWhatsAppBody("");
    setWhatsAppTemplateOpen(false);
    setWhatsAppTemplateForm(emptyWhatsAppTemplateForm);
  }, [selectedConversation?.id]);

  const openCreate = () => {
    setConversationForm(emptyConversationForm);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setConversationForm(emptyConversationForm);
    setModalOpen(false);
  };

  const handleConversationFieldChange = (field, value) => {
    setConversationForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleMessageFieldChange = (field, value) => {
    setMessageForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleTaskFieldChange = (field, value) => {
    setTaskForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleContactFieldChange = (field, value) => {
    setContactForm((prev) => ({ ...prev, [field]: value }));
  };

  const openTaskCreate = () => {
    if (!selectedConversation?.id) return;
    setTaskForm({
      ...emptyTaskForm,
      title: `${copy.taskTypes?.follow_up || "Follow-up"}: ${selectedConversation.subject}`,
    });
    setTaskModalOpen(true);
  };

  const closeTaskModal = () => {
    if (taskSaving) return;
    setTaskModalOpen(false);
    setTaskForm(emptyTaskForm);
  };

  const openContactCreateFromCall = () => {
    if (!selectedConversation?.id || selectedConversation.contact) return;
    const phone = getConversationPhone(selectedConversation);
    setContactForm({
      ...emptyContactForm,
      phone: phone || "",
      company_id: selectedConversation.company_id || "",
    });
    setContactModalOpen(true);
  };

  const closeContactModal = () => {
    if (contactSaving) return;
    setContactModalOpen(false);
    setContactForm(emptyContactForm);
  };

  const handleConversationSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");

    const payload = {
      ...conversationForm,
      provider: conversationForm.provider || null,
      contact_customer_id: conversationForm.contact_customer_id || null,
      company_id: conversationForm.company_id || null,
      lead_id: conversationForm.lead_id || null,
    };

    try {
      const created = await createCrmConversation(payload);
      setModalOpen(false);
      setConversationForm(emptyConversationForm);
      setSelectedConversation(created);
      await loadConversations();
    } catch (err) {
      console.error("[CRM] Conversation save error:", err);
      setError(err?.response?.data?.error || pageCopy.saveError);
    } finally {
      setSaving(false);
    }
  };

  const handleMessageSubmit = async (event) => {
    event.preventDefault();
    if (!selectedConversation?.id) return;
    setMessageSaving(true);
    setError("");

    try {
      await createCrmConversationMessage(selectedConversation.id, messageForm);
      setMessageForm(emptyMessageForm);
      await loadConversationDetail(selectedConversation.id);
      await loadConversations();
    } catch (err) {
      console.error("[CRM] Message save error:", err);
      setError(err?.response?.data?.error || pageCopy.messageSaveError);
    } finally {
      setMessageSaving(false);
    }
  };

  const handleWhatsAppSubmit = async (event) => {
    event.preventDefault();
    if (!selectedConversation?.id || !whatsAppBody.trim()) return;

    setWhatsAppSending(true);
    setError("");
    setNotice("");

    try {
      const response = await sendCrmWhatsAppMessage(selectedConversation.id, {
        body: whatsAppBody,
      });
      setWhatsAppBody("");
      if (response?.conversation) {
        setSelectedConversation(response.conversation);
      } else {
        await loadConversationDetail(selectedConversation.id);
      }
      await loadConversations();
      setNotice(pageCopy.whatsappSent);
    } catch (err) {
      console.error("[CRM] WhatsApp message send error:", err);
      const data = err?.response?.data || {};
      if (data.template_required) {
        setWhatsAppTemplateOpen(true);
      }
      setError(data.error || pageCopy.whatsappSendError);
    } finally {
      setWhatsAppSending(false);
    }
  };

  const handleWhatsAppTemplateFieldChange = (key, value) => {
    setWhatsAppTemplateForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleWhatsAppTemplateSubmit = async (event) => {
    event.preventDefault();
    if (!selectedConversation?.id || !whatsAppTemplateForm.template_name.trim()) return;

    setWhatsAppTemplateSending(true);
    setError("");
    setNotice("");

    try {
      const response = await sendCrmWhatsAppTemplate(selectedConversation.id, {
        template_name: whatsAppTemplateForm.template_name,
        language_code: whatsAppTemplateForm.language_code || "es_ES",
        parameters: whatsAppTemplateForm.parameters,
      });
      setWhatsAppTemplateForm(emptyWhatsAppTemplateForm);
      setWhatsAppTemplateOpen(false);
      if (response?.conversation) {
        setSelectedConversation(response.conversation);
      } else {
        await loadConversationDetail(selectedConversation.id);
      }
      await loadConversations();
      setNotice(pageCopy.whatsappTemplateSent);
    } catch (err) {
      console.error("[CRM] WhatsApp template send error:", err);
      setError(err?.response?.data?.error || pageCopy.whatsappTemplateSendError);
    } finally {
      setWhatsAppTemplateSending(false);
    }
  };

  const handleGenerateSummary = async () => {
    if (!selectedConversation?.id) return;
    setSummarySaving(true);
    setError("");
    setNotice("");

    try {
      await createCrmConversationSummary(selectedConversation.id, { language: languageId });
      await loadConversationDetail(selectedConversation.id);
      await loadConversations();
      setNotice(pageCopy.summaryGenerated);
    } catch (err) {
      console.error("[CRM] Summary generation error:", err);
      setError(err?.response?.data?.error || pageCopy.summaryGenerateError);
    } finally {
      setSummarySaving(false);
    }
  };

  const handleTaskSubmit = async (event) => {
    event.preventDefault();
    if (!selectedConversation?.id) return;
    setTaskSaving(true);
    setError("");

    try {
      await createCrmTask({
        ...taskForm,
        due_at: taskForm.due_at || null,
        conversation_id: selectedConversation.id,
        lead_id: selectedConversation.lead_id || null,
        contact_customer_id: selectedConversation.contact_customer_id || null,
        company_id: selectedConversation.company_id || null,
      });
      setTaskModalOpen(false);
      setTaskForm(emptyTaskForm);
      await loadConversationDetail(selectedConversation.id);
    } catch (err) {
      console.error("[CRM] Conversation task save error:", err);
      setError(err?.response?.data?.error || copy.entities.tasks.saveError);
    } finally {
      setTaskSaving(false);
    }
  };

  const handleRelinkContact = async () => {
    if (!selectedConversation?.id || relinkingContact) return;
    setRelinkingContact(true);
    setError("");
    setNotice("");

    try {
      const result = await relinkCrmConversationContact(selectedConversation.id);
      if (result?.conversation) {
        setSelectedConversation(result.conversation);
      }
      let nextNotice = pageCopy.noMatchingContact;
      if (result?.linked) {
        nextNotice = pageCopy.linkContactSuccess;
      } else if (result?.reason === "already_linked") {
        nextNotice = pageCopy.alreadyLinkedContact;
      }
      await loadConversations();
      setNotice(nextNotice);
    } catch (err) {
      console.error("[CRM] Conversation contact relink error:", err);
      setError(err?.response?.data?.error || pageCopy.linkContactError);
    } finally {
      setRelinkingContact(false);
    }
  };

  const handleContactCreateSubmit = async (event) => {
    event.preventDefault();
    if (!selectedConversation?.id) return;
    const conversationId = selectedConversation.id;
    setContactSaving(true);
    setError("");
    setNotice("");

    const payload = {
      ...contactForm,
      company_id: contactForm.company_id || null,
      source: contactForm.source || "call",
      crm_status: contactForm.crm_status || "active",
    };

    try {
      const created = await createCrmContact(payload);
      let linkResult = null;
      try {
        linkResult = await relinkCrmConversationContact(conversationId);
      } catch (linkErr) {
        console.warn("[CRM] Contact created, relink refresh failed:", linkErr);
      }
      setContactModalOpen(false);
      setContactForm(emptyContactForm);
      await loadLookups();
      await loadConversationDetail(conversationId);
      await loadConversations();

      const linked =
        linkResult?.linked ||
        linkResult?.reason === "already_linked" ||
        Number(created?.crm_linked_conversations_count || 0) > 0;
      setNotice(linked ? pageCopy.createContactLinkSuccess : pageCopy.createContactSuccess);
    } catch (err) {
      console.error("[CRM] Contact from call create error:", err);
      setError(err?.response?.data?.error || pageCopy.createContactError);
    } finally {
      setContactSaving(false);
    }
  };

  const selectedContactLabel = getConversationContactLabel(selectedConversation, copy);
  const selectedContactContext = getRelatedContextLabel(selectedConversation);
  const selectedConversationPhone = getConversationPhone(selectedConversation);
  const selectedAiSummary = selectedConversation?.ai_summaries?.[0] || null;
  const selectedAiSummaryText = formatAiSummaryValue(selectedAiSummary?.summary);
  const selectedAiNextSteps = formatAiSummaryValue(selectedAiSummary?.next_steps);
  const selectedIsWhatsApp = isWhatsAppConversation(selectedConversation);

  return (
    <main className="crm-page crm-page--compactTable crm-page--activity">
      <header className="crm-pageHeader">
        <div>
          <p className="crm-eyebrow">{copy.common.crm}</p>
          <h1>{copy.sections.activity.title}</h1>
        </div>

        <button type="button" className="crm-ghostButton" onClick={openCreate}>
          <FiPlus aria-hidden="true" />
          <span>{pageCopy.newTitle}</span>
        </button>
      </header>

      <section className="crm-panel">
        <div className="crm-entityToolbar">
          <label className="crm-searchBox">
            <FiSearch aria-hidden="true" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") loadConversations();
              }}
              placeholder={pageCopy.searchPlaceholder}
            />
          </label>
          <button type="button" className="crm-ghostButton" onClick={loadConversations} disabled={loading}>
            <FiRefreshCw aria-hidden="true" />
            <span>{loading ? copy.common.loading : copy.common.search}</span>
          </button>
        </div>

        {error ? <div className="crm-stateBanner is-error">{error}</div> : null}
        {!error && notice ? <div className="crm-stateBanner">{notice}</div> : null}

        <div className="crm-activityLayout">
          <aside className="crm-conversationList" aria-label={pageCopy.conversationTitle}>
            {rows.length ? (
              rows.map((conversation) => (
                <button
                  key={conversation.id}
                  type="button"
                  className={`crm-conversationRow ${
                    selectedConversation?.id === conversation.id ? "crm-conversationRow--active" : ""
                  }`}
                  onClick={() => loadConversationDetail(conversation.id)}
                >
                  <span className="crm-conversationRowTitle">{conversation.subject}</span>
                  <span className="crm-conversationRowMeta">
                    {copy.conversationChannels?.[conversation.channel] || conversation.channel}
                    {" · "}
                    {getConversationContactLabel(conversation, copy)}
                    {getRelatedContextLabel(conversation) ? ` · ${getRelatedContextLabel(conversation)}` : ""}
                  </span>
                  <span className="crm-conversationRowDate">
                    {formatDateTime(conversation.last_message_at || conversation.updated_at || conversation.created_at)}
                  </span>
                </button>
              ))
            ) : (
              <div className="crm-tableEmpty">{loading ? copy.common.loading : pageCopy.empty}</div>
            )}
          </aside>

          <section className="crm-conversationDetail">
            {selectedConversation ? (
              <>
                <div className="crm-conversationDetailHeader">
                  <div>
                    <p className="crm-panelKicker">
                      {copy.conversationChannels?.[selectedConversation.channel] || selectedConversation.channel}
                    </p>
                    <h2>{selectedConversation.subject}</h2>
                    <span>{selectedContactLabel}</span>
                  </div>
                  <div className="crm-conversationDetailActions">
                    <button type="button" className="crm-ghostButton" onClick={openTaskCreate}>
                      <FiPlus aria-hidden="true" />
                      <span>{pageCopy.newTask}</span>
                    </button>
                    <span className={`crm-statusPill crm-statusPill--${selectedConversation.status || "open"}`}>
                      {pageCopy.status[selectedConversation.status] || selectedConversation.status}
                    </span>
                  </div>
                </div>

                <div className={`crm-contactLinkBox ${selectedConversation.contact ? "" : "crm-contactLinkBox--missing"}`}>
                  <div className="crm-contactLinkMain">
                    <span className="crm-contactLinkIcon">
                      <FiUser aria-hidden="true" />
                    </span>
                    <div>
                      <strong>{pageCopy.contactLabel}</strong>
                      <span>{selectedContactLabel}</span>
                      {selectedConversation.contact ? (
                        <small>
                          {[selectedConversation.contact.email, selectedConversation.contact.phone].filter(Boolean).join(" · ") ||
                            pageCopy.contactLinked}
                        </small>
                      ) : (
                        <small>
                          {[pageCopy.noLinkedContactHint, selectedConversationPhone].filter(Boolean).join(" · ")}
                        </small>
                      )}
                      {selectedContactContext && selectedConversation.contact ? <small>{selectedContactContext}</small> : null}
                    </div>
                  </div>
                  {!selectedConversation.contact ? (
                    <div className="crm-contactLinkActions">
                      <button type="button" className="crm-ghostButton" onClick={openContactCreateFromCall}>
                        <FiPlus aria-hidden="true" />
                        <span>{pageCopy.createContactFromCall}</span>
                      </button>
                      <button
                        type="button"
                        className="crm-ghostButton"
                        onClick={handleRelinkContact}
                        disabled={relinkingContact}
                      >
                        <FiLink aria-hidden="true" />
                        <span>{relinkingContact ? pageCopy.linkingContact : pageCopy.linkMatchingContact}</span>
                      </button>
                    </div>
                  ) : null}
                </div>

                <div className="crm-aiSummaryBox">
                  <div className="crm-aiSummaryHeader">
                    <strong>{selectedAiSummaryText ? pageCopy.aiSummaryTitle : pageCopy.aiReady}</strong>
                    <button
                      type="button"
                      className="crm-ghostButton"
                      onClick={handleGenerateSummary}
                      disabled={summarySaving || detailLoading || !selectedConversation.messages?.length}
                    >
                      <FiRefreshCw aria-hidden="true" />
                      <span>{summarySaving ? pageCopy.generatingSummary : pageCopy.generateSummary}</span>
                    </button>
                  </div>
                  {selectedAiSummaryText ? <p>{selectedAiSummaryText}</p> : null}
                  {selectedAiNextSteps ? (
                    <p className="crm-aiSummaryMeta">
                      <strong>{pageCopy.nextStepsLabel}</strong>
                      <span>{selectedAiNextSteps}</span>
                    </p>
                  ) : null}
                  {selectedAiSummary?.sentiment ? (
                    <span className="crm-aiSummarySentiment">
                      {pageCopy.sentimentLabel}: {selectedAiSummary.sentiment}
                    </span>
                  ) : null}
                </div>

                <div className="crm-relatedTasksBox">
                  <div className="crm-relatedTasksHeader">
                    <h3>{copy.sections.tasks.title}</h3>
                  </div>
                  {relatedTasks.length ? (
                    relatedTasks.map((task) => (
                      <div key={task.id} className="crm-relatedTaskRow">
                        <div>
                          <strong>{task.title}</strong>
                          <span>
                            {copy.taskTypes?.[task.task_type] || task.task_type}
                            {" · "}
                            {task.due_at ? formatDateTime(task.due_at) : copy.entities.tasks.dueLabel}
                          </span>
                        </div>
                        <span className={`crm-statusPill crm-statusPill--${task.priority || "normal"}`}>
                          {copy.taskPriorities?.[task.priority] || task.priority}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="crm-relatedTasksEmpty">{copy.dashboard.emptyTasks}</div>
                  )}
                </div>

                <div className="crm-messageTimeline">
                  {detailLoading ? (
                    <div className="crm-tableEmpty">{copy.common.loading}</div>
                  ) : selectedConversation.messages?.length ? (
                    selectedConversation.messages.map((message) => (
                      <article key={message.id} className={`crm-messageBubble crm-messageBubble--${message.direction}`}>
                        <div className="crm-messageBubbleMeta">
                          <span>{copy.conversationMessageTypes?.[message.message_type] || message.message_type}</span>
                          <span>{copy.conversationDirections?.[message.direction] || message.direction}</span>
                          <span>{formatDateTime(message.occurred_at)}</span>
                        </div>
                        {message.body ? <p>{formatCallMessageBody(message, copy)}</p> : null}
                        {message.recording_url ? (
                          <div className="crm-recordingPlayer">
                            <audio controls preload="none" src={message.recording_url}>
                              <a href={message.recording_url} target="_blank" rel="noreferrer">
                                Recording
                              </a>
                            </audio>
                            <a href={message.recording_url} target="_blank" rel="noreferrer">
                              Open recording
                            </a>
                          </div>
                        ) : null}
                        {Array.isArray(message.attachments) && message.attachments.length ? (
                          <div className="crm-messageAttachments">
                            {message.attachments.map((attachment) => (
                              <a
                                key={attachment.id || attachment.file_url}
                                href={attachment.file_url}
                                target="_blank"
                                rel="noreferrer"
                                className={`crm-messageAttachment ${
                                  isImageAttachment(attachment) ? "crm-messageAttachment--image" : ""
                                }`}
                              >
                                {isImageAttachment(attachment) ? (
                                  <img src={attachment.file_url} alt={getAttachmentLabel(attachment, copy)} loading="lazy" />
                                ) : null}
                                <span>
                                  {isImageAttachment(attachment)
                                    ? getAttachmentLabel(attachment, copy)
                                    : `${pageCopy.openAttachment}: ${getAttachmentLabel(attachment, copy)}`}
                                </span>
                              </a>
                            ))}
                          </div>
                        ) : null}
                      </article>
                    ))
                  ) : (
                    <div className="crm-tableEmpty">{copy.common.noMessages || copy.common.noRecords}</div>
                  )}
                </div>

                {selectedIsWhatsApp ? (
                  <>
                    <form className="crm-whatsappComposer" onSubmit={handleWhatsAppSubmit}>
                      <textarea
                        value={whatsAppBody}
                        onChange={(event) => setWhatsAppBody(event.target.value)}
                        placeholder={pageCopy.whatsappPlaceholder}
                        rows={3}
                      />
                      <div className="crm-modalActions">
                        <button
                          type="button"
                          className="crm-ghostButton"
                          onClick={() => setWhatsAppTemplateOpen((prev) => !prev)}
                        >
                          <FiMessageSquare aria-hidden="true" />
                          <span>{pageCopy.whatsappTemplate}</span>
                        </button>
                        <button
                          type="submit"
                          className="crm-primaryButton"
                          disabled={whatsAppSending || !whatsAppBody.trim()}
                        >
                          <FiSend aria-hidden="true" />
                          <span>{whatsAppSending ? pageCopy.whatsappSending : pageCopy.whatsappSend}</span>
                        </button>
                      </div>
                    </form>

                    {whatsAppTemplateOpen ? (
                      <form className="crm-whatsappTemplateComposer" onSubmit={handleWhatsAppTemplateSubmit}>
                        <div className="crm-messageComposerControls">
                          <Field
                            label={pageCopy.whatsappTemplateName}
                            value={whatsAppTemplateForm.template_name}
                            onChange={(value) => handleWhatsAppTemplateFieldChange("template_name", value)}
                            required
                          />
                          <Field
                            label={pageCopy.whatsappTemplateLanguage}
                            value={whatsAppTemplateForm.language_code}
                            onChange={(value) => handleWhatsAppTemplateFieldChange("language_code", value)}
                          />
                        </div>
                        <Field
                          label={pageCopy.whatsappTemplateParameters}
                          value={whatsAppTemplateForm.parameters}
                          onChange={(value) => handleWhatsAppTemplateFieldChange("parameters", value)}
                          textarea
                        />
                        <div className="crm-modalActions">
                          <button
                            type="submit"
                            className="crm-primaryButton"
                            disabled={whatsAppTemplateSending || !whatsAppTemplateForm.template_name.trim()}
                          >
                            <FiSend aria-hidden="true" />
                            <span>{whatsAppTemplateSending ? pageCopy.whatsappSending : pageCopy.whatsappTemplateSend}</span>
                          </button>
                        </div>
                      </form>
                    ) : null}
                  </>
                ) : null}

                <form className="crm-messageComposer" onSubmit={handleMessageSubmit}>
                  <div className="crm-messageComposerControls">
                    <SelectField
                      label={pageCopy.fields.direction}
                      value={messageForm.direction}
                      onChange={(value) => handleMessageFieldChange("direction", value)}
                      options={directionOptions}
                    />
                    <SelectField
                      label={pageCopy.fields.message_type}
                      value={messageForm.message_type}
                      onChange={(value) => handleMessageFieldChange("message_type", value)}
                      options={messageTypeOptions}
                    />
                  </div>
                  <Field
                    label={pageCopy.fields.body}
                    value={messageForm.body}
                    textarea
                    required
                    onChange={(value) => handleMessageFieldChange("body", value)}
                  />
                  <div className="crm-modalActions">
                    <button type="submit" className="crm-primaryButton" disabled={messageSaving}>
                      <FiMessageSquare aria-hidden="true" />
                      <span>{messageSaving ? copy.common.saving : pageCopy.addMessage}</span>
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="crm-emptyList">{pageCopy.noConversationSelected}</div>
            )}
          </section>
        </div>
      </section>

      {modalOpen ? (
        <div className="crm-modalBackdrop" role="presentation">
          <form className="crm-modal" onSubmit={handleConversationSubmit}>
            <div className="crm-modalHeader">
              <h2>{pageCopy.newTitle}</h2>
              <button type="button" className="crm-iconButton" onClick={closeModal} aria-label={copy.common.cancel}>
                <FiX aria-hidden="true" />
              </button>
            </div>

            <div className="crm-formGrid">
              <Field
                label={pageCopy.fields.subject}
                value={conversationForm.subject}
                required
                onChange={(value) => handleConversationFieldChange("subject", value)}
              />
              <SelectField
                label={pageCopy.fields.channel}
                value={conversationForm.channel}
                onChange={(value) => handleConversationFieldChange("channel", value)}
                options={channelOptions}
              />
              <SelectField
                label={pageCopy.fields.status}
                value={conversationForm.status}
                onChange={(value) => handleConversationFieldChange("status", value)}
                options={statusOptions}
              />
              <Field
                label={pageCopy.fields.provider}
                value={conversationForm.provider}
                onChange={(value) => handleConversationFieldChange("provider", value)}
              />
              <SelectField
                label={pageCopy.fields.lead_id}
                value={conversationForm.lead_id}
                onChange={(value) => handleConversationFieldChange("lead_id", value)}
                options={[
                  { value: "", label: copy.common.noLead },
                  ...leads.map((lead) => ({ value: lead.id, label: lead.title })),
                ]}
              />
              <SelectField
                label={pageCopy.fields.contact_customer_id}
                value={conversationForm.contact_customer_id}
                onChange={(value) => handleConversationFieldChange("contact_customer_id", value)}
                options={[
                  { value: "", label: copy.common.noContact },
                  ...contacts.map((contact) => ({ value: contact.id, label: getContactLabel(contact) })),
                ]}
              />
              <SelectField
                label={pageCopy.fields.company_id}
                value={conversationForm.company_id}
                onChange={(value) => handleConversationFieldChange("company_id", value)}
                options={[
                  { value: "", label: copy.common.noCompany },
                  ...companies.map((company) => ({ value: company.id, label: company.name })),
                ]}
              />
            </div>

            <div className="crm-modalActions">
              <button type="button" className="crm-ghostButton" onClick={closeModal} disabled={saving}>
                {copy.common.cancel}
              </button>
              <button type="submit" className="crm-primaryButton" disabled={saving}>
                {saving ? copy.common.saving : copy.common.save}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {contactModalOpen ? (
        <div className="crm-modalBackdrop" role="presentation">
          <form className="crm-modal" onSubmit={handleContactCreateSubmit}>
            <div className="crm-modalHeader">
              <h2>{pageCopy.createContactTitle}</h2>
              <button type="button" className="crm-iconButton" onClick={closeContactModal} aria-label={copy.common.cancel}>
                <FiX aria-hidden="true" />
              </button>
            </div>

            <div className="crm-formGrid">
              <Field
                label={copy.entities.contacts.fields.first_name}
                value={contactForm.first_name}
                onChange={(value) => handleContactFieldChange("first_name", value)}
              />
              <Field
                label={copy.entities.contacts.fields.last_name}
                value={contactForm.last_name}
                onChange={(value) => handleContactFieldChange("last_name", value)}
              />
              <Field
                label={copy.entities.contacts.fields.email}
                value={contactForm.email}
                type="email"
                onChange={(value) => handleContactFieldChange("email", value)}
              />
              <Field
                label={copy.entities.contacts.fields.phone}
                value={contactForm.phone}
                required
                onChange={(value) => handleContactFieldChange("phone", value)}
              />
              <SelectField
                label={copy.entities.contacts.fields.company_id}
                value={contactForm.company_id}
                onChange={(value) => handleContactFieldChange("company_id", value)}
                options={[
                  { value: "", label: copy.common.noCompany },
                  ...companies.map((company) => ({ value: company.id, label: company.name })),
                ]}
              />
              <Field
                label={copy.entities.contacts.fields.job_title}
                value={contactForm.job_title}
                onChange={(value) => handleContactFieldChange("job_title", value)}
              />
              <Field
                label={copy.entities.contacts.fields.notes}
                value={contactForm.notes}
                textarea
                onChange={(value) => handleContactFieldChange("notes", value)}
              />
            </div>

            <div className="crm-modalActions">
              <button type="button" className="crm-ghostButton" onClick={closeContactModal} disabled={contactSaving}>
                {copy.common.cancel}
              </button>
              <button type="submit" className="crm-primaryButton" disabled={contactSaving}>
                {contactSaving ? copy.common.saving : pageCopy.createContact}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {taskModalOpen ? (
        <div className="crm-modalBackdrop" role="presentation">
          <form className="crm-modal" onSubmit={handleTaskSubmit}>
            <div className="crm-modalHeader">
              <h2>{copy.entities.tasks.newTitle}</h2>
              <button type="button" className="crm-iconButton" onClick={closeTaskModal} aria-label={copy.common.cancel}>
                <FiX aria-hidden="true" />
              </button>
            </div>

            <div className="crm-formGrid">
              <Field
                label={copy.entities.tasks.fields.title}
                value={taskForm.title}
                required
                onChange={(value) => handleTaskFieldChange("title", value)}
              />
              <SelectField
                label={copy.entities.tasks.fields.task_type}
                value={taskForm.task_type}
                onChange={(value) => handleTaskFieldChange("task_type", value)}
                options={taskTypeOptions}
              />
              <SelectField
                label={copy.entities.tasks.fields.priority}
                value={taskForm.priority}
                onChange={(value) => handleTaskFieldChange("priority", value)}
                options={taskPriorityOptions}
              />
              <Field
                label={copy.entities.tasks.fields.due_at}
                value={taskForm.due_at}
                type="datetime-local"
                onChange={(value) => handleTaskFieldChange("due_at", value)}
              />
              <Field
                label={copy.entities.tasks.fields.description}
                value={taskForm.description}
                textarea
                onChange={(value) => handleTaskFieldChange("description", value)}
              />
            </div>

            <div className="crm-modalActions">
              <button type="button" className="crm-ghostButton" onClick={closeTaskModal} disabled={taskSaving}>
                {copy.common.cancel}
              </button>
              <button type="submit" className="crm-primaryButton" disabled={taskSaving}>
                {taskSaving ? copy.common.saving : copy.common.save}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </main>
  );
}

function Field({ label, value, onChange, type = "text", textarea = false, required = false }) {
  return (
    <label className={`crm-field ${textarea ? "crm-field--wide" : ""}`}>
      <span>{label}</span>
      {textarea ? (
        <textarea value={value} onChange={(event) => onChange(event.target.value)} rows={4} required={required} />
      ) : (
        <input type={type} value={value} onChange={(event) => onChange(event.target.value)} required={required} />
      )}
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label className="crm-field">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}
